/** @jsxImportSource @emotion/react */
import { ExclamationCircleOutlined, UploadOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  List,
  message,
  Modal,
  Progress,
  Select,
  Space,
  Spin,
  Upload,
} from "antd";
import { RcFile } from "antd/lib/upload";
import Axios from "axios";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import {
  CreateInstallPackage,
  InstallPackageList,
  ProductNameContract,
  UploadComplete,
} from "src/models/installation";
import { InstallationServiceClient } from "src/services/InstallationService";
const { Option } = Select;

export default React.memo(
  (props: {
    visible: boolean;
    onClose: () => void;
    packageData: InstallPackageList;
    list: () => void;
  }) => {
    const { visible, onClose, packageData, list } = props;
    const formatMessage = useFormatMessage();
    const uploadingFiles = useRef<RcFile[]>([]);
    const extSuccess = useRef<number>(0);
    const [form] = Form.useForm();
    const [mainDataSource, setMainDataSource] = useState<
      { fileName: string; fileSize: number; display: boolean }[]
    >([]);
    const [extDataSource, setExtDataSource] = useState<
      { order: number; fileName: string; fileSize: number; display: boolean }[]
    >([]);
    const [isNext, setIsNext] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [publishLoading, setPublishLoading] = useState(false);
    const [id, setId] = useState("");

    const [packageFiles, setPackageFiles] = useState<
      { mainFile: string; extFiles?: string[] }[]
    >([]);
    const [spreadDataSource, setSpreadDataSource] = useState<
      InstallPackageList[]
    >([]);
    const [mainProgress, setMainProgress] = useState(0);
    const [extOneProgress, setExtOneProgress] = useState(0);
    const [extTwoProgress, setExtTwoProgress] = useState(0);
    const [mainDisplay, setMainDisplay] = useState(false);
    const [extOneDisplay, setExtOneDisplay] = useState(false);
    const [extTwoDisplay, setExtTwoDisplay] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    useEffect(() => {
      if (visible) {
        form.setFieldsValue({
          packageName: packageData?.packageName,
          productName: packageData?.productName,
          sku: packageData?.sku,
        });
        const files: string[] = [];
        const mainAndExt: { mainFile: string; extFiles?: string[] }[] = [];
        if (packageData.extFiles) {
          // eslint-disable-next-line array-callback-return
          Object.keys(packageData.extFiles).map((item) => {
            files.push(item);
          });
          mainAndExt.push({
            mainFile: packageData?.fileName as string,
            extFiles: files,
          });
        } else {
          mainAndExt.push({
            mainFile: packageData?.fileName as string,
          });
        }
        setPackageFiles(mainAndExt);
      }
    }, [form, packageData, visible]);

    const onNextStep = useCallback(async () => {
      const formValue = await form.validateFields();
      setFormLoading(true);
      const formContent: CreateInstallPackage = {
        packageName: formValue.packageName,
        productName: formValue.productName,
        sku: formValue.sku,
        version: formValue.version,
        description: formValue.description,
      };
      //创建安装包
      InstallationServiceClient.create(2, formContent).then((res) => {
        setId(res.data.packageInfo?.id);
        message.success(
          formatMessage(messageIds.installation.common.createSuccessfully)
        );
        setFormLoading(false);
        setIsNext(true);
      });
    }, [form, formatMessage]);

    const onPreStep = useCallback(() => {
      setIsNext(false);
    }, []);

    const onCloseCurrent = useCallback(() => {
      onClose();
      setFormLoading(false);
      setIsNext(false);
      setMainDisplay(false);
      setExtOneDisplay(false);
      setExtTwoDisplay(false);
      setCanDelete(false);
      setMainProgress(0);
      setExtOneProgress(0);
      setExtTwoProgress(0);
      setMainDataSource([]);
      setExtDataSource([]);
      setSpreadDataSource([]);
      extSuccess.current = 0;
      form.resetFields();
    }, [form, onClose]);

    const onPublish = useCallback(() => {
      if (isNext && (mainDataSource.length > 0 || extDataSource.length > 0)) {
        if (spreadDataSource.length > 0) {
          Modal.confirm({
            title: formatMessage(messageIds.common.tip),
            icon: <ExclamationCircleOutlined />,
            content: formatMessage(
              messageIds.installation.common.publishPackage,
              {
                package: packageData?.packageName as string,
              }
            ),
            okText: formatMessage(messageIds.common.confirm),
            cancelText: formatMessage(messageIds.common.cancel),
            onOk() {
              setPublishLoading(true);
              InstallationServiceClient.publishInstallation(2, id).then(() => {
                message.success(
                  formatMessage(
                    messageIds.installation.common.publishSuccessfully
                  )
                );
                setPublishLoading(false);
                list();
                onCloseCurrent();
              });
            },
          });
        } else {
          message.error(
            formatMessage(messageIds.installation.common.inComplete)
          );
        }
      }
    }, [
      extDataSource.length,
      formatMessage,
      id,
      isNext,
      list,
      mainDataSource.length,
      onCloseCurrent,
      packageData,
      spreadDataSource.length,
    ]);

    const fileUpload = useCallback(
      (
        type: string,
        url: string,
        file: RcFile,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        configs: Record<string, any>
      ) => {
        Axios.put(`${url}`, file, configs).then(() => {
          if (type === "main") {
            message.success(
              formatMessage(messageIds.installation.common.uploadSuccessfully, {
                fileName: file?.name,
                type: formatMessage(messageIds.installation.common.mainFile),
              })
            );

            const data = [];
            data.push({
              fileName: file?.name,
              fileSize: file?.size,
              display: true,
            });
            setMainDataSource(data);
            setTimeout(() => setMainDisplay(true), 800);

            if (
              ProductNameContract[packageData.productName] === "Studio" ||
              ProductNameContract[packageData.productName] === "Robot"
            ) {
              if (extSuccess.current === 2) {
                const completed: UploadComplete = {
                  fileName: file?.name,
                  fileSize: file?.size,
                  isMainfileNotice: true,
                };
                InstallationServiceClient.notice(2, id, completed).then(
                  (response) => {
                    const data = [];
                    data.push(response.data as InstallPackageList);
                    setSpreadDataSource(data);
                    setCanDelete(true);
                  }
                );
              } else {
                return;
              }
            } else if (
              ProductNameContract[packageData.productName] ===
                "AndroidAutomation" ||
              ProductNameContract[packageData.productName] ===
                "IOSAutomation" ||
              ProductNameContract[packageData.productName] ===
                "AndroidAutomationV2" ||
              ProductNameContract[packageData.productName] === "OCROffline"
            ) {
              const completed: UploadComplete = {
                fileName: file?.name,
                fileSize: file?.size,
                isMainfileNotice: true,
              };
              InstallationServiceClient.notice(2, id, completed).then(
                (response) => {
                  const data = [];
                  data.push(response.data as InstallPackageList);
                  setSpreadDataSource(data);
                }
              );
            }
          } else if (type === "extra") {
            message.success(
              formatMessage(messageIds.installation.common.uploadSuccessfully, {
                fileName: file?.name,
                type: formatMessage(messageIds.installation.common.extFile),
              })
            );

            const data = extDataSource?.filter(
              (item) => item.fileName !== file?.name
            );
            data.push({
              order: extSuccess.current + 1,
              fileName: file?.name,
              fileSize: file?.size,
              display: true,
            });
            setExtDataSource(data);
            extSuccess.current === 0 &&
              setTimeout(() => setExtOneDisplay(true), 800);
            extSuccess.current === 1 &&
              setTimeout(() => setExtTwoDisplay(true), 800);

            const extCompleted: UploadComplete = {
              fileName: file?.name,
              fileSize: file?.size,
              isMainfileNotice: false,
            };
            InstallationServiceClient.notice(2, id, extCompleted).then(() => {
              extSuccess.current = extSuccess.current + 1;

              if (extSuccess.current === 2 && mainDataSource.length > 0) {
                const completed: UploadComplete = {
                  fileName: mainDataSource[0]?.fileName,
                  fileSize: mainDataSource[0]?.fileSize,
                  isMainfileNotice: true,
                };
                InstallationServiceClient.notice(2, id, completed).then(
                  (response) => {
                    const data = [];
                    data.push(response.data as InstallPackageList);
                    setSpreadDataSource(data);
                    setCanDelete(true);
                  }
                );
              }
            });
          }
        });
      },
      [
        extDataSource,
        formatMessage,
        id,
        mainDataSource,
        packageData.productName,
      ]
    );

    const uploadPackages = useCallback(
      (type, files: RcFile[]) => {
        // eslint-disable-next-line array-callback-return
        files.map((file) => {
          if (
            ProductNameContract[packageData.productName] === "Studio" ||
            ProductNameContract[packageData.productName] === "Robot"
          ) {
            if (type === "main") {
              const data = [];
              data.push({
                fileName: file?.name,
                fileSize: file?.size,
                display: false,
              });
              setMainDataSource(data);
              InstallationServiceClient.getUrl(
                2,
                id,
                file.name,
                file.size
              ).then((resp) => {
                const url = resp.data?.uris[0]?.uri as string;
                const headers = resp.data?.uris[0]?.headers as Record<
                  string,
                  string
                >;
                const configs = {
                  headers: headers,
                  withCredentials: false,
                  onUploadProgress: (progress: { loaded: number }) => {
                    const { loaded } = progress;
                    const fileSize = file?.size;
                    const percentage = (loaded / fileSize) * 100;
                    setMainProgress(percentage);
                  },
                };
                fileUpload("main", url, file, configs);
              });
            } else if (type === "extra") {
              const existnupkg = extDataSource?.filter(
                (item) =>
                  item.fileName.endsWith("nupkg") &&
                  file?.name.endsWith("nupkg")
              );
              const existRELEASES = extDataSource?.filter(
                (item) =>
                  item.fileName === file?.name && file?.name === "RELEASES"
              );
              const notPermit =
                file?.name.endsWith("nupkg") || file?.name === "RELEASES";
              if (!notPermit) {
                message.error(
                  formatMessage(messageIds.installation.common.errorAllowed, {
                    file: file?.name,
                  })
                );
              } else {
                if (existnupkg.length > 0) {
                  message.error(
                    formatMessage(messageIds.installation.common.errorSuffix, {
                      suffix: "nupkg",
                    })
                  );
                } else if (existRELEASES.length > 0) {
                  message.error(
                    formatMessage(messageIds.installation.common.errorSuffix, {
                      suffix: "RELEASES",
                    })
                  );
                } else {
                  const data = extDataSource?.filter(
                    (item) => item.fileName !== file?.name
                  );
                  data.push({
                    order: extSuccess.current + 1,
                    fileName: file?.name,
                    fileSize: file?.size,
                    display: false,
                  });
                  setExtDataSource(data);
                  InstallationServiceClient.getUrl(
                    2,
                    id,
                    file?.name,
                    file?.size
                  ).then((resp) => {
                    const url = resp.data?.uris[0]?.uri as string;
                    const headers = resp.data?.uris[0]?.headers as Record<
                      string,
                      string
                    >;
                    const configs = {
                      headers: headers,
                      withCredentials: false,
                      onUploadProgress: (progress: { loaded: number }) => {
                        const { loaded } = progress;
                        const fileSize = file?.size;
                        const percentage = (loaded / fileSize) * 100;
                        extSuccess.current === 0 &&
                          setExtOneProgress(percentage);
                        extSuccess.current === 1 &&
                          setExtTwoProgress(percentage);
                      },
                    };
                    fileUpload("extra", url, file, configs);
                  });
                }
              }
            }
          } else if (
            ProductNameContract[packageData.productName] ===
              "AndroidAutomation" ||
            ProductNameContract[packageData.productName] === "IOSAutomation" ||
            ProductNameContract[packageData.productName] ===
              "AndroidAutomationV2" ||
            ProductNameContract[packageData.productName] === "OCROffline"
          ) {
            const existZip = mainDataSource?.filter(
              (item) =>
                item.fileName === file?.name || item.fileName.endsWith("zip")
            );
            if (existZip.length > 0) {
              message.error(
                formatMessage(messageIds.installation.common.errorSuffix, {
                  suffix: "zip",
                })
              );
            } else {
              const data = [];
              data.push({
                fileName: file?.name,
                fileSize: file?.size,
                display: false,
              });
              setMainDataSource(data);
              InstallationServiceClient.getUrl(
                2,
                id,
                file.name,
                file.size
              ).then((resp) => {
                const url = resp.data?.uris[0]?.uri as string;
                const headers = resp.data?.uris[0]?.headers as Record<
                  string,
                  string
                >;
                const configs = {
                  headers: headers,
                  withCredentials: false,
                  onUploadProgress: (progress: { loaded: number }) => {
                    const { loaded } = progress;
                    const fileSize = file?.size;
                    const percentage = (loaded / fileSize) * 100;
                    setMainProgress(percentage);
                  },
                };
                fileUpload("main", url, file, configs);
              });
            }
          }
        });
      },
      [
        extDataSource,
        fileUpload,
        formatMessage,
        id,
        mainDataSource,
        packageData.productName,
      ]
    );

    const onMainDelete = useCallback(
      (record) => {
        if (extDataSource.length > 0) {
          message.error(
            formatMessage(messageIds.installation.common.firstRemove)
          );
        } else {
          Modal.confirm({
            title: formatMessage(messageIds.common.tip),
            icon: <ExclamationCircleOutlined />,
            content: formatMessage(
              messageIds.common.info.deletionConfirmation,
              {
                entityTypeName: formatMessage(
                  messageIds.installation.common.mainFile
                ),
                name: record.fileName,
              }
            ),
            okText: formatMessage(messageIds.common.confirm),
            cancelText: formatMessage(messageIds.common.cancel),
            onOk() {
              if (extDataSource.length > 0) {
                message.error(
                  formatMessage(messageIds.installation.common.firstRemove)
                );
              } else {
                if (
                  ProductNameContract[packageData.productName] === "Studio" ||
                  ProductNameContract[packageData.productName] === "Robot"
                ) {
                  if (canDelete) {
                    InstallationServiceClient.deleteMainFile(2, id).then(() => {
                      message.success(
                        formatMessage(
                          messageIds.common.info.deleteSuccessfully,
                          {
                            entityTypeName: formatMessage(
                              messageIds.installation.common.mainFile
                            ),
                            name: record.fileName,
                          }
                        )
                      );
                      setMainDataSource(
                        mainDataSource.filter(
                          (item) => item.fileName !== record.fileName
                        )
                      );
                    });
                  } else {
                    message.success(
                      formatMessage(messageIds.common.info.deleteSuccessfully, {
                        entityTypeName: formatMessage(
                          messageIds.installation.common.mainFile
                        ),
                        name: record.fileName,
                      })
                    );
                    setMainDataSource([]);
                  }
                } else if (
                  ProductNameContract[packageData.productName] ===
                    "AndroidAutomation" ||
                  ProductNameContract[packageData.productName] ===
                    "IOSAutomation" ||
                  ProductNameContract[packageData.productName] ===
                    "AndroidAutomationV2" ||
                  ProductNameContract[packageData.productName] === "OCROffline"
                ) {
                  InstallationServiceClient.deleteMainFile(2, id).then(() => {
                    message.success(
                      formatMessage(messageIds.common.info.deleteSuccessfully, {
                        entityTypeName: formatMessage(
                          messageIds.installation.common.mainFile
                        ),
                        name: record.fileName,
                      })
                    );
                    setMainDataSource(
                      mainDataSource.filter(
                        (item) => item.fileName !== record.fileName
                      )
                    );
                  });
                }
              }
            },
          });
        }
      },
      [
        canDelete,
        extDataSource.length,
        formatMessage,
        id,
        mainDataSource,
        packageData.productName,
      ]
    );

    const onExtDelete = useCallback(
      (record) => {
        Modal.confirm({
          title: formatMessage(messageIds.common.tip),
          icon: <ExclamationCircleOutlined />,
          content: formatMessage(messageIds.common.info.deletionConfirmation, {
            entityTypeName: formatMessage(
              messageIds.installation.common.extFile
            ),
            name: record.fileName,
          }),
          okText: formatMessage(messageIds.common.confirm),
          cancelText: formatMessage(messageIds.common.cancel),
          onOk() {
            InstallationServiceClient.deleteExtFiles(
              2,
              id,
              record.fileName
            ).then(() => {
              message.success(
                formatMessage(messageIds.common.info.deleteSuccessfully, {
                  entityTypeName: formatMessage(
                    messageIds.installation.common.extFile
                  ),
                  name: record.fileName,
                })
              );
              setExtDataSource(
                extDataSource.filter(
                  (item) => item.fileName !== record.fileName
                )
              );
              extSuccess.current = extSuccess.current - 1;
            });
          },
        });
      },
      [extDataSource, formatMessage, id]
    );

    const formContent = useMemo(
      () => (
        <Fragment>
          <Form layout="vertical" form={form}>
            <Form.Item
              label={formatMessage(messageIds.installation.common.packageName)}
              name="packageName"
              rules={[{ required: true }]}
            >
              <Input></Input>
            </Form.Item>
            <Form.Item
              label={formatMessage(messageIds.installation.common.productName)}
              name="productName"
            >
              <Select disabled>
                <Option value="Robot">
                  {formatMessage(messageIds.installation.common.robot)}
                </Option>
                <Option value="Studio">
                  {formatMessage(messageIds.installation.common.studio)}
                </Option>
                <Option value="AndroidAutomation">
                  {formatMessage(messageIds.installation.common.android)}
                </Option>
                <Option value="AndroidAutomationV2">
                  {formatMessage(messageIds.installation.common.appium)}
                </Option>
                <Option value="IOSAutomation">
                  {formatMessage(messageIds.installation.common.ios)}
                </Option>
                <Option value="OCROffline">
                  {formatMessage(messageIds.installation.common.ocr)}
                </Option>
              </Select>
            </Form.Item>
            <Form.Item
              label={formatMessage(messageIds.installation.common.sku)}
              name="sku"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="Community">
                  {formatMessage(
                    messageIds.installation.common.version.community
                  )}
                </Option>
                <Option value="Enterprise">
                  {formatMessage(
                    messageIds.installation.common.version.enterprise
                  )}
                </Option>
              </Select>
            </Form.Item>
            <Form.Item
              label={formatMessage(messageIds.installation.common.versionId)}
              name="version"
              rules={[{ required: true }]}
            >
              <Input></Input>
            </Form.Item>
            <Form.Item label={formatMessage(messageIds.common.description)}>
              <Input.TextArea rows={3}></Input.TextArea>
            </Form.Item>
          </Form>
          <div
            css={css`
              text-align: right;
            `}
          >
            <Button type="primary" onClick={onNextStep}>
              {formatMessage(messageIds.installation.common.create)}
            </Button>
          </div>
        </Fragment>
      ),
      [form, formatMessage, onNextStep]
    );

    const publishContent = useMemo(() => {
      return (
        <div>
          {packageFiles &&
            (packageFiles[0]?.extFiles ? (
              <Alert
                type="info"
                message={`${formatMessage(
                  messageIds.installation.common.mainTip,
                  { file: packageFiles[0].mainFile }
                )}，${formatMessage(messageIds.installation.common.extTip, {
                  extOne: packageFiles[0].extFiles[0],
                  extTwo: packageFiles[0].extFiles[1],
                })}`}
                css={css`
                  .ant-alert-message {
                    color: #6699cc !important;
                  }
                `}
              ></Alert>
            ) : (
              <Alert
                type="info"
                message={formatMessage(messageIds.installation.common.mainTip, {
                  file: packageFiles[0]?.mainFile,
                })}
                css={css`
                  .ant-alert-message {
                    color: #6699cc !important;
                  }
                `}
              ></Alert>
            ))}

          <List
            header={
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                `}
              >
                <span
                  css={css`
                    color: #6699cc;
                  `}
                >
                  {formatMessage(messageIds.installation.common.mainFile)}
                </span>

                <Upload
                  showUploadList={false}
                  accept={
                    ProductNameContract[packageData.productName] ===
                    "AndroidAutomation"
                      ? ".zip"
                      : ProductNameContract[packageData.productName] ===
                        "IOSAutomation"
                      ? ".zip"
                      : ProductNameContract[packageData.productName] ===
                        "Studio"
                      ? ".exe"
                      : ProductNameContract[packageData.productName] === "Robot"
                      ? ".exe"
                      : ProductNameContract[packageData.productName] ===
                        "AndroidAutomationV2"
                      ? ".beta"
                      : ProductNameContract[packageData.productName] ===
                        "OCROffline"
                      ? ".beta"
                      : "*"
                  }
                  beforeUpload={(file, fileList) => {
                    const uploadingFile = uploadingFiles.current.find(
                      (uploadingFile) => uploadingFile.uid === file.uid
                    );

                    if (!uploadingFile) {
                      uploadingFiles.current = fileList;
                      uploadPackages("main", fileList);
                    }

                    return false;
                  }}
                >
                  <Button type="primary" icon={<UploadOutlined />}>
                    {formatMessage(messageIds.common.upload)}
                  </Button>
                </Upload>
              </div>
            }
            dataSource={mainDataSource}
            renderItem={(data) => (
              <List.Item>
                <div
                  css={css`
                    width: 100%;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      justify-content: space-between;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                      `}
                    >
                      {formatMessage(messageIds.installation.common.fileName)} :{" "}
                      {data.fileName}
                    </div>
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                      `}
                    >
                      {formatMessage(messageIds.installation.common.fileSize)} :{" "}
                      {data.fileSize}
                    </div>
                    <div
                      css={css`
                        width: 15%;
                      `}
                    >
                      {data.display && (
                        <Button type="link" onClick={() => onMainDelete(data)}>
                          {formatMessage(messageIds.common.delete)}
                        </Button>
                      )}
                    </div>
                  </div>
                  {data.display && mainDisplay ? (
                    ""
                  ) : (
                    <Progress
                      type="line"
                      percent={mainProgress}
                      strokeWidth={3}
                      strokeColor={{
                        "0%": "#108ee9",
                        "100%": "#87d068",
                      }}
                      format={(percent) => `${percent} %`}
                    ></Progress>
                  )}
                </div>
              </List.Item>
            )}
          ></List>
          {packageFiles[0]?.extFiles && (
            <List
              header={
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                  `}
                >
                  <span
                    css={css`
                      color: #6699cc;
                    `}
                  >
                    {formatMessage(messageIds.installation.common.extFile)}
                  </span>

                  <Upload
                    showUploadList={false}
                    beforeUpload={(file, fileList) => {
                      const uploadingFile = uploadingFiles.current.find(
                        (uploadingFile) => uploadingFile.uid === file.uid
                      );

                      if (!uploadingFile) {
                        uploadingFiles.current = fileList;
                        uploadPackages("extra", fileList);
                      }

                      return false;
                    }}
                  >
                    <Button type="primary" icon={<UploadOutlined />}>
                      {formatMessage(messageIds.common.upload)}
                    </Button>
                  </Upload>
                </div>
              }
              dataSource={extDataSource}
              renderItem={(data) => (
                <List.Item>
                  <div
                    css={css`
                      width: 100%;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        justify-content: space-between;
                      `}
                    >
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          overflow: hidden;
                          text-overflow: ellipsis;
                          width: 42.5%;
                        `}
                      >
                        {formatMessage(messageIds.installation.common.fileName)}{" "}
                        : {data.fileName}
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          overflow: hidden;
                          text-overflow: ellipsis;
                          width: 42.5%;
                        `}
                      >
                        {formatMessage(messageIds.installation.common.fileSize)}{" "}
                        : {data.fileSize}
                      </div>
                      <div
                        css={css`
                          width: 15%;
                        `}
                      >
                        {data.display && (
                          <Button type="link" onClick={() => onExtDelete(data)}>
                            {formatMessage(messageIds.common.delete)}
                          </Button>
                        )}
                      </div>
                    </div>
                    {data.order === 1 &&
                      (data.display && extOneDisplay ? (
                        ""
                      ) : (
                        <Progress
                          type="line"
                          percent={extOneProgress}
                          strokeWidth={3}
                          strokeColor={{
                            "0%": "#108ee9",
                            "100%": "#87d068",
                          }}
                          format={(percent) => `${percent} %`}
                        ></Progress>
                      ))}
                    {data.order === 2 &&
                      (data.display && extTwoDisplay ? (
                        ""
                      ) : (
                        <Progress
                          type="line"
                          percent={extTwoProgress}
                          strokeWidth={3}
                          strokeColor={{
                            "0%": "#108ee9",
                            "100%": "#87d068",
                          }}
                          format={(percent) => `${percent} %`}
                        ></Progress>
                      ))}
                  </div>
                </List.Item>
              )}
            ></List>
          )}

          <div
            css={css`
              text-align: right;
              margin-top: 40px;
            `}
          >
            <Space size={20}>
              <Button onClick={onPreStep}>
                {formatMessage(messageIds.common.preStep)}
              </Button>
              <Button type="primary" onClick={onPublish}>
                {formatMessage(messageIds.installation.common.publish)}
              </Button>
            </Space>
          </div>
        </div>
      );
    }, [
      extDataSource,
      extOneDisplay,
      extOneProgress,
      extTwoDisplay,
      extTwoProgress,
      formatMessage,
      mainDataSource,
      mainDisplay,
      mainProgress,
      onExtDelete,
      onMainDelete,
      onPreStep,
      onPublish,
      packageData.productName,
      packageFiles,
      uploadPackages,
    ]);

    return (
      <div css={css``}>
        <Modal
          visible={visible}
          title={formatMessage(messageIds.installation.common.update)}
          footer={""}
          onCancel={onCloseCurrent}
          css={css`
            .ant-modal-header {
              border-bottom: none;
            }
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: center;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                margin-bottom: 20px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  max-width: 200px;
                  min-width: 90px;
                  text-align: center;
                `}
              >
                <span
                  css={css`
                    width: 20px;
                    line-height: 20px;
                    color: white;
                    background: rgb(51, 119, 255);
                    border-radius: 50%;
                  `}
                >
                  1
                </span>
                <span
                  css={css`
                    margin-left: 8px;
                    color: rgb(51, 119, 255);
                  `}
                >
                  {formatMessage(
                    messageIds.installation.common.packageInformation
                  )}
                </span>
              </div>
              <Divider
                css={css`
                  flex: 1 1 0%;
                  max-width: 200px;
                  min-width: 100px;
                  margin: 0px 20px;
                  border-top: ${isNext
                    ? "1px solid rgb(51, 119, 255)"
                    : "1px solid rgb(28, 32, 41)"};
                `}
              ></Divider>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  max-width: 200px;
                  min-width: 90px;
                  text-align: center;
                `}
              >
                <span
                  css={css`
                    width: 20px;
                    line-height: 20px;
                    color: white;
                    background: ${isNext
                      ? "rgb(51,119,255)"
                      : "rgb(143,155,179)"};
                    border-radius: 50%;
                  `}
                >
                  2
                </span>
                <span
                  css={css`
                    margin-left: 8px;
                    color: ${isNext ? "rgb(51,119,255)" : "rgb(143, 155, 179)"};
                  `}
                >
                  {formatMessage(messageIds.installation.common.packageUpload)}
                </span>
              </div>
            </div>
          </div>

          {isNext ? (
            <div>
              {publishLoading ? (
                <Spin
                  tip="Loading..."
                  css={css`
                    position: initial !important;
                  `}
                >
                  {publishContent}
                </Spin>
              ) : (
                publishContent
              )}
            </div>
          ) : (
            <div>
              {formLoading ? (
                <Spin
                  tip="Loading..."
                  css={css`
                    position: initial !important;
                  `}
                >
                  {formContent}
                </Spin>
              ) : (
                formContent
              )}
            </div>
          )}
        </Modal>
      </div>
    );
  }
);
